
// i18n static dictionary and helpers

export type LangCode = 'sv' | 'en' | 'pl' | 'de' | 'ru' | 'es';

export const SUPPORTED_LANGS: LangCode[] = ['sv', 'en', 'pl', 'de', 'ru', 'es'];

import enJson from './dictionaries/en.json';
import plJson from './dictionaries/pl.json';
import deJson from './dictionaries/de.json';
import ruJson from './dictionaries/ru.json';
import esJson from './dictionaries/es.json';
import svJson from './dictionaries/sv.json';
import { supabase } from '@/integrations/supabase/client';

// Normalize text keys: trim and collapse whitespace
export const normalizeText = (s: string): string => (s || '')
  .replace(/\s+/g, ' ')
  .trim();

// Base static dictionary (seed a few common UI strings)
// Keys are original Swedish text (normalized)
const BASE_STATIC_DICTIONARY: Record<string, Record<string, string>> = {
  en: {
    [normalizeText('Välj språk')]: 'Choose language',
    [normalizeText('Laddar...')]: 'Loading...',
    [normalizeText('Tillbaka')]: 'Back',
    [normalizeText('Översättningsinställningar')]: 'Translation settings',
    [normalizeText('Språkinställningar')]: 'Language settings',
    [normalizeText('Välj vilket språk du vill använda')]: 'Choose which language you want to use',
    [normalizeText('Ditt språkval sparas och kommer att användas nästa gång du besöker sidan.')]: 'Your language selection is saved and will be used next time you visit.',
  },
  pl: {},
  de: {},
  ru: {},
  es: {},
  sv: {},
};

const IMPORTED_STATIC: Record<string, Record<string, string>> = {
  en: (enJson as Record<string, string>) || {},
  pl: (plJson as Record<string, string>) || {},
  de: (deJson as Record<string, string>) || {},
  ru: (ruJson as Record<string, string>) || {},
  es: (esJson as Record<string, string>) || {},
  sv: (svJson as Record<string, string>) || {},
};

export const STATIC_DICTIONARY: Record<string, Record<string, string>> = {
  en: { ...BASE_STATIC_DICTIONARY.en, ...IMPORTED_STATIC.en },
  pl: { ...BASE_STATIC_DICTIONARY.pl, ...IMPORTED_STATIC.pl },
  de: { ...BASE_STATIC_DICTIONARY.de, ...IMPORTED_STATIC.de },
  ru: { ...BASE_STATIC_DICTIONARY.ru, ...IMPORTED_STATIC.ru },
  es: { ...BASE_STATIC_DICTIONARY.es, ...IMPORTED_STATIC.es },
  sv: { ...BASE_STATIC_DICTIONARY.sv, ...IMPORTED_STATIC.sv },
};


// Runtime dictionary (stored in localStorage) so admins can add entries without code changes
const RUNTIME_KEY = 'static_dictionary_runtime_v1';

type DictMap = Record<string, Record<string, string>>;

const readRuntime = (): DictMap => {
  try {
    const raw = localStorage.getItem(RUNTIME_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as DictMap;
  } catch {
    return {};
  }
};

const writeRuntime = (map: DictMap) => {
  try {
    localStorage.setItem(RUNTIME_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

export const getRuntimeDictionaryFor = (lang: string): Record<string, string> => {
  const all = readRuntime();
  return all[lang] || {};
};

export const mergeIntoRuntimeDictionary = (lang: string, entries: Record<string, string>) => {
  const all = readRuntime();
  const current = all[lang] || {};
  const merged: Record<string, string> = { ...current };
  for (const [svText, tr] of Object.entries(entries)) {
    const key = normalizeText(svText);
    if (!key) continue;
    merged[key] = tr;
  }
  all[lang] = merged;
  writeRuntime(all);
};
// Cloud/shared dictionary (stored in Supabase, cached in localStorage)
const CLOUD_KEY_PREFIX = 'static_dictionary_cloud_v1_';
const CLOUD_FETCHED_AT_PREFIX = 'static_dictionary_cloud_fetched_at_v1_';
const CLOUD_TTL_MS = 1000 * 60 * 60 * 24; // 24h

const readCloud = (lang: string): Record<string, string> => {
  try {
    const raw = localStorage.getItem(CLOUD_KEY_PREFIX + lang);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
};

const writeCloud = (lang: string, map: Record<string, string>) => {
  try {
    localStorage.setItem(CLOUD_KEY_PREFIX + lang, JSON.stringify(map));
    localStorage.setItem(CLOUD_FETCHED_AT_PREFIX + lang, Date.now().toString());
  } catch {
    // ignore
  }
};

const isCloudFresh = (lang: string) => {
  const ts = localStorage.getItem(CLOUD_FETCHED_AT_PREFIX + lang);
  if (!ts) return false;
  const age = Date.now() - Number(ts);
  return age < CLOUD_TTL_MS;
};

export const getCloudDictionaryFor = (lang: string): Record<string, string> => {
  return readCloud(lang);
};

export const fetchAndCacheCloudDictionary = async (lang: string, force = false): Promise<Record<string, string>> => {
  if (!force && isCloudFresh(lang)) {
    return readCloud(lang);
  }
  const { data, error } = await supabase
    .from('i18n_translations')
    .select('sv_key, value')
    .eq('lang', lang)
    .limit(20000);
  if (error) {
    console.error('Failed to fetch cloud dictionary', error);
    return readCloud(lang);
  }
  const map: Record<string, string> = {};
  for (const row of data || []) {
    const key = normalizeText((row as any).sv_key || '');
    if (!key) continue;
    map[key] = (row as any).value || '';
  }
  writeCloud(lang, map);
  return map;
};

export const upsertCloudTranslations = async (lang: string, entries: Record<string, string>) => {
  const payload = Object.entries(entries).map(([svText, value]) => ({
    lang,
    sv_key: normalizeText(svText),
    value
  }));
  const { error } = await supabase
    .from('i18n_translations')
    .upsert(payload, { onConflict: 'lang,sv_key' });
  if (error) throw error;
  // refresh cache after upsert
  await fetchAndCacheCloudDictionary(lang, true);
};

export const getCombinedDictionary = (lang: string): Record<string, string> => {
  const staticDict = STATIC_DICTIONARY[lang] || {};
  const runtimeDict = getRuntimeDictionaryFor(lang) || {};
  const cloudDict = getCloudDictionaryFor(lang) || {};
  // Cloud overrides runtime/static
  return { ...staticDict, ...runtimeDict, ...cloudDict };
};

export const getStaticTranslation = (lang: string, originalSvText: string): string | undefined => {
  const dict = getCombinedDictionary(lang);
  const key = normalizeText(originalSvText);
  return dict[key];
};
